import axios, { AxiosError, AxiosHeaders, InternalAxiosRequestConfig } from 'axios';
import queryString from 'query-string';

import { AnyObject } from 'types/UtilityTypes';
import { getAccessToken } from 'utils/authTokens';
import { logError } from 'utils/logError';
import { notifyAuthRequired } from 'utils/nativeBridge';
import { isMobileWebView } from 'utils/nativeEnv';

import { isUnauthorizedError } from './errorUtils';
import {
  ApiCallParams,
  ApiErrorResponse,
  AuthAxiosRequestConfig,
  BaseResponse,
  RequestOptions,
  RequestParams,
} from './types';

const apiClient = axios.create();

const normalizeStringData = (raw: string): string | AnyObject => {
  try {
    const response = JSON.parse(raw);
    const isStringData = typeof response.data === 'string';

    if (isStringData) {
      const data = JSON.parse(response.data);

      return {
        ...response,
        data,
      };
    }

    return response;
  } catch (e) {
    return raw;
  }
};

const callApi = async <ResponseData>({
  endpoint: url,
  method = 'GET',
  config = {},
  abortController,
}: ApiCallParams<ResponseData>): Promise<BaseResponse<ResponseData>> => {
  try {
    const response = await apiClient({
      url,
      method,
      transformResponse: [normalizeStringData],
      signal: abortController?.signal,
      ...(config as AuthAxiosRequestConfig),
    });

    const { data } = response.data;

    return {
      isError: false,
      data,
    };
  } catch (error) {
    return {
      isError: true,
      error: error as AxiosError<ApiErrorResponse>,
    };
  }
};

const configApi = ({
  method = 'GET',
  data = {},
  config = {},
  multipartFormData = false,
}: RequestOptions): AuthAxiosRequestConfig => {
  const queryConfig: AuthAxiosRequestConfig = { ...config };

  if ((queryConfig.data === null || queryConfig.data === undefined) && method.toUpperCase() !== 'GET') {
    queryConfig.data = data;
  }

  if (!queryConfig.headers) {
    queryConfig.headers = {};
  }

  if (multipartFormData) {
    const formData = new FormData();

    Object.keys(data).forEach((key) => {
      const value = data[key];

      if (Array.isArray(value)) {
        value.forEach((item) => {
          formData.append(key, item);
        });
      } else {
        formData.append(key, value);
      }
    });
    queryConfig.data = formData;

    Object.assign(queryConfig.headers, {
      'Content-Type': 'multipart/form-data',
    });
  }

  if (method.toUpperCase() === 'GET') {
    queryConfig.params = {
      ...data,
    };

    queryConfig.paramsSerializer = {
      ...queryConfig.paramsSerializer,
      serialize: (params) => queryString.stringify(params, { skipNull: true }),
    };
  }

  return queryConfig;
};

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const authConfig = config as InternalAxiosRequestConfig & AuthAxiosRequestConfig;

  if (authConfig._skipAuth || !isMobileWebView()) {
    return authConfig;
  }

  const accessToken = getAccessToken();

  if (accessToken) {
    const headers = new AxiosHeaders(authConfig.headers);

    headers.set('Authorization', `Bearer ${accessToken}`);
    authConfig.headers = headers;
  }

  return authConfig;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorResponse>) => {
    const requestConfig = (error.config || {}) as InternalAxiosRequestConfig & AuthAxiosRequestConfig;

    if (!requestConfig._skipAuth && isMobileWebView() && isUnauthorizedError(error)) {
      notifyAuthRequired();
    }

    return Promise.reject(error);
  }
);

const call = async <ResponseData>(params: RequestParams<ResponseData>): Promise<BaseResponse<ResponseData>> => {
  const { endpoint, method, data, config, multipartFormData, mockResponse, abortController } = params;

  if (mockResponse) {
    return mockResponse;
  }

  try {
    return await callApi<ResponseData>({
      endpoint,
      method,
      config: configApi({ method, data, config, multipartFormData }),
      abortController,
    });
  } catch (err) {
    const error = err as AxiosError<ApiErrorResponse>;
    const errorStatus = error?.response?.status;
    const logMessage = `[API]: ${method} ${endpoint} END ERROR ${errorStatus}`;

    logError(logMessage);

    return {
      isError: true,
      error,
    };
  }
};

export default call;
